import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    CircularProgress,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { clinicalModel2 } from '../Axios/AxiosConfig';
import VisibilityIcon from '@mui/icons-material/Visibility';

const ClinicalScreeningPage: React.FC = () => {
    const [tabValue, setTabValue] = useState(0);
    const [users, setUsers] = useState<any[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setPage(0);
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            // Fetch a large number of users to perform client-side filtering and pagination
            // This ensures we can fill each tab's pages correctly (e.g. 10 per page)
            const response = await clinicalModel2.get('express_interest_forms', {
                params: {
                    page: 1, // Get from the start
                    limit: 5000 // Fetch a large number to get "all" data for client-side splitting
                }
            });

            const data = response.data?.data || response.data || [];
            if (Array.isArray(data)) {
                const sortedData = [...data].sort((a, b) => {
                    const dateA = new Date(a.createdAt || a.created_at || a.timestamp || a.date || 0).getTime();
                    const dateB = new Date(b.createdAt || b.created_at || b.timestamp || b.date || 0).getTime();
                    return dateB - dateA; // Descending order (latest first)
                });
                setUsers(sortedData);
            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []); // Only fetch once on mount

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const clinicalKeywords = [
        'gastric health',
        'metabolic health',
        "women's health",
        'diabetes / pre-diabetes',
        'pre diabetes',
        'prediabetes',
        'Diabetic',
        'Pre-Diabetic',
    ];

    const isClinicalUser = (user: any) => {
        // Check form_type first - if it's 'clinical', user goes to Clinical tab
        // form_type is an object like { name: 'clinical', id: 1 }
        const formType = typeof user.form_type === 'object'
            ? user.form_type?.name
            : user.form_type;

        if (formType && String(formType).toLowerCase().trim() === 'clinical') {
            return true;
        }

        // Otherwise, check wellness interests
        const raw = user.wellness_Interest ?? user.wellness_interest;

        let userInterests: string[] = [];

        if (Array.isArray(raw)) {
            userInterests = raw.map(s => String(s).trim().toLowerCase()).filter(Boolean);
        } else if (typeof raw === 'string') {
            userInterests = raw.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
        } else if (raw) {
            // Handle cases like objects or numbers by converting to string
            userInterests = String(raw).split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
        }

        // If no interests, not clinical
        if (userInterests.length === 0) return false;

        // Check if ALL of the user's interests are clinical
        // If even ONE interest is not in the clinical list, they go to Corporate tab
        return userInterests.every((userInterest) =>
            clinicalKeywords.some((keyword) => userInterest.includes(keyword))
        );
    };

    const filteredUsers = users.filter((user) => {
        const clinical = isClinicalUser(user);
        return tabValue === 0 ? clinical : !clinical;
    });

    const handlePaidClick = (userId: string) => {
        setSelectedUserId(userId);
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
        setSelectedUserId(null);
    };

    const confirmPaidStatus = async () => {
        if (!selectedUserId) return;

        try {
            setLoading(true);
            const payload = {
                form_type: { id: 2, name: "paid" },
            };

            await clinicalModel2.put(`/express_interest_forms/${encodeURIComponent(selectedUserId)}`, payload);

            // Refresh data after successful update
            await fetchUsers();

            handleCloseConfirmDialog();
        } catch (error) {
            console.error("Error updating user status:", error);
            // Optionally handle error (e.g., show toast)
        } finally {
            setLoading(false);
        }
    };

    // Client-side pagination: Slice the filtered list
    const displayedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);


    return (
        <Box sx={{ p: 3, pt: 1.5 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontFamily: "Libre Baskerville", color: "#3B362A", mb: 2 }}>
                Clinical Screening
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={handleChangeTab} aria-label="clinical screening tabs">
                    <Tab label="Clinical Users" />
                    <Tab label="Corporate Users" />
                </Tabs>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sl No</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Wellness Interest</TableCell>
                                    <TableCell>Created At</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayedUsers.length > 0 ? (
                                    displayedUsers.map((user, index) => (
                                        <TableRow hover role="checkbox" tabIndex={-1} key={user._id || index}>
                                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell>{user.name || '-'}</TableCell>
                                            <TableCell>{user.email || '-'}</TableCell>
                                            <TableCell>{user.phone || '-'}</TableCell>
                                            <TableCell>
                                                {String(user.wellness_Interest || user.wellness_interest || '-')
                                                    .split(',')
                                                    .map((interest: string, i: number) => (
                                                        <Box key={i} component="div" sx={{ display: 'block' }}>
                                                            {interest.trim()}
                                                        </Box>
                                                    ))
                                                }
                                            </TableCell>
                                            <TableCell>
                                                {user.createdAt || user.created_at || user.timestamp || user.date
                                                    ? new Date(user.createdAt || user.created_at || user.timestamp || user.date).toLocaleString('en-IN', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small">
                                                    <VisibilityIcon />
                                                </IconButton>
                                                {tabValue === 1 && (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => handlePaidClick(user._id || user.id)}
                                                        sx={{ ml: 1, textTransform: 'none' }}
                                                    >
                                                        Paid
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No users available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={users.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}

            <Dialog
                open={confirmDialogOpen}
                onClose={handleCloseConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Confirm Payment Status"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to mark this user as paid?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={confirmPaidStatus} color="primary" autoFocus variant="contained">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClinicalScreeningPage;